import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, ChartLineUp, Wallet, Cpu, Trophy, Scan, Target as TargetIcon,
  ShieldCheck, ArrowRight, Warning, Sparkle, Broadcast, EnvelopeOpen,
  Receipt, Plus, ChartPieSlice,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const formatINR = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
  if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

// Division accent palette (per design guidelines / already established in landing)
const ACCENT = {
  crm:        { color: "#0D9488", label: "Sales & CRM",           tag: "REVENUE"    },
  finance:    { color: "#10B981", label: "Finance & Accounting",  tag: "CASH FLOW"  },
  procurement:{ color: "#F59E0B", label: "Procurement",           tag: "SUPPLY"     },
  hr:         { color: "#7C3AED", label: "HR & Performance",      tag: "PEOPLE"     },
  mis:        { color: "#3B82F6", label: "MIS Productivity",      tag: "OPERATIONS" },
  tracker:    { color: "#D90429", label: "Shelves-Track",         tag: "ACCOUNTABILITY" },
  admin:      { color: "#B45309", label: "Admin Control",         tag: "GOVERNANCE" },
};

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

function DivisionTile({ id, kpis, ctas, icon: Icon, primary }) {
  const a = ACCENT[id];
  const navigate = useNavigate();
  return (
    <div
      data-testid={`division-tile-${id}`}
      className="group relative overflow-hidden border border-black/10 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(0,0,0,0.35)] cursor-pointer flex flex-col"
      onClick={() => primary && navigate(primary.to)}
    >
      {/* Accent bar */}
      <div className="absolute top-0 left-0 h-1 w-full" style={{ background: a.color }} />
      {/* Watermark icon */}
      <Icon size={128} weight="thin" className="absolute -right-6 -bottom-6 opacity-[0.05]" style={{ color: a.color }} />

      <div className="flex items-start justify-between mb-5 relative z-10">
        <div>
          <div className="font-mono-shelv text-[10px] tracking-[0.25em] uppercase font-bold" style={{ color: a.color }}>
            {a.tag}
          </div>
          <h3 className="font-display text-2xl font-black tracking-tight mt-1 text-[#0A0A0A]">
            {a.label}
          </h3>
        </div>
        <div className="w-10 h-10 flex items-center justify-center" style={{ background: `${a.color}12` }}>
          <Icon size={20} weight="bold" style={{ color: a.color }} />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 mb-5 relative z-10">
        {kpis.map((k, i) => (
          <div key={i} data-testid={`kpi-${id}-${i}`}>
            <div className="text-[10px] tracking-widest uppercase text-[#717680] font-mono-shelv">{k.label}</div>
            <div className="font-display text-2xl font-bold text-[#0A0A0A] mt-0.5 leading-tight">
              {k.value ?? "—"}
            </div>
            {k.hint && <div className="text-xs text-[#717680] mt-0.5">{k.hint}</div>}
          </div>
        ))}
      </div>

      {/* CTAs */}
      <div className="mt-auto flex flex-wrap gap-2 relative z-10">
        {ctas.map((c, i) => (
          <button
            key={i}
            data-testid={`cta-${id}-${i}`}
            onClick={(e) => { e.stopPropagation(); navigate(c.to); }}
            className="text-xs font-semibold px-3 py-1.5 border border-black/10 hover:bg-[#F5F6F8] transition flex items-center gap-1.5"
          >
            {c.label}
            <ArrowRight size={12} weight="bold" />
          </button>
        ))}
      </div>
    </div>
  );
}

function AlertPill({ testId, count, amount, color, label, onClick }) {
  if (!count) return null;
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-3 bg-white border border-black/10 hover:bg-[#FAFBFC] transition text-left group"
    >
      <div className="w-8 h-8 flex items-center justify-center" style={{ background: `${color}18` }}>
        <Warning size={16} weight="bold" style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="font-display font-bold text-[#0A0A0A] leading-none">
          {count}
          {amount ? <span className="text-sm font-semibold text-[#717680] ml-1.5">· {formatINR(amount)}</span> : null}
        </div>
        <div className="text-[11px] tracking-widest uppercase font-mono-shelv text-[#717680] mt-1">{label}</div>
      </div>
      <ArrowRight size={14} weight="bold" className="ml-auto text-[#717680] group-hover:translate-x-0.5 transition" />
    </button>
  );
}

function QuickAction({ testId, icon: Icon, label, to, accent, onClick }) {
  const navigate = useNavigate();
  return (
    <button
      data-testid={testId}
      onClick={onClick || (() => navigate(to))}
      className="flex items-center gap-2.5 px-4 py-2.5 border border-black/10 bg-white hover:-translate-y-0.5 hover:shadow-md transition-all"
      style={{ boxShadow: `inset 3px 0 0 ${accent}` }}
    >
      <Icon size={16} weight="bold" style={{ color: accent }} />
      <span className="text-sm font-semibold text-[#0A0A0A]">{label}</span>
    </button>
  );
}

export default function CommandCenter() {
  const { user, hasRole, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const now = useClock();

  useEffect(() => {
    let mounted = true;
    api.get("/command-center/summary")
      .then((r) => { if (mounted) setSummary(r.data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const greeting = useMemo(() => {
    const h = now.getHours();
    if (h < 5) return "Working late";
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    if (h < 21) return "Good evening";
    return "Good night";
  }, [now]);

  const firstName = (user?.name || "").split(" ")[0] || "Founder";
  const dateStr = now.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const timeStr = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });

  const d = summary?.divisions || {};
  const alerts = summary?.alerts || {};

  const showCrm = hasRole("crm", "viewer");
  const showFinance = hasRole("finance", "viewer");
  const showHr = hasRole("hr", "viewer") || isAdmin;
  const showMis = hasRole("mis", "viewer");
  const showTracker = hasRole("tracker", "viewer") || isAdmin;

  return (
    <Layout title="Command Center" subtitle="Every division. One dashboard.">
      {/* HERO */}
      <div className="relative bg-[#0A0A0A] text-white p-8 md:p-10 mb-8 overflow-hidden" data-testid="command-hero">
        {/* red slash accent */}
        <div className="absolute -right-24 -top-24 w-[420px] h-[420px] rotate-12 opacity-20" style={{ background: "linear-gradient(135deg,#D90429 0%,transparent 60%)" }} />
        <div className="absolute left-0 bottom-0 h-[3px] w-2/3 bg-[#D90429]" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="font-mono-shelv text-[10px] tracking-[0.35em] uppercase text-[#D90429] font-bold mb-2">
              REYANSH · MULTI-DIVISION ERP
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-none">
              {greeting}, <span className="text-[#D90429]">{firstName}</span>.
            </h1>
            <p className="text-white/60 text-sm md:text-base mt-3 max-w-xl">
              Sales, Finance, Procurement, HR, MIS &amp; Ops — a single command surface for everything Shelvestech runs.
            </p>
          </div>
          <div className="text-left md:text-right" data-testid="command-clock">
            <div className="font-mono-shelv text-[10px] tracking-widest uppercase text-white/50">Live · IST</div>
            <div className="font-display text-3xl md:text-4xl font-bold tracking-tight tabular-nums">{timeStr}</div>
            <div className="text-xs text-white/60 mt-1">{dateStr}</div>
          </div>
        </div>
      </div>

      {/* QUICK ACTIONS STRIP */}
      <div className="mb-8" data-testid="quick-actions">
        <div className="font-mono-shelv text-[10px] tracking-[0.25em] uppercase text-[#717680] font-bold mb-3">Quick Actions</div>
        <div className="flex flex-wrap gap-2">
          {showCrm && <QuickAction testId="qa-new-lead" icon={Plus} label="New Lead" to="/leads" accent="#0D9488" />}
          {showCrm && <QuickAction testId="qa-new-proposal" icon={Plus} label="New Proposal" to="/proposals/new" accent="#0D9488" />}
          {showFinance && <QuickAction testId="qa-record-payment" icon={Wallet} label="Record Payment" to="/finance/work-orders" accent="#10B981" />}
          {showMis && <QuickAction testId="qa-log-mis" icon={Scan} label="Log MIS" to="/mis/daily" accent="#3B82F6" />}
          {showCrm && <QuickAction testId="qa-send-email" icon={EnvelopeOpen} label="Send Email" to="/email" accent="#0D9488" />}
          {showCrm && <QuickAction testId="qa-swarm" icon={Broadcast} label="AI Swarm" to="/swarm" accent="#D90429" />}
          {showFinance && <QuickAction testId="qa-invoice" icon={Receipt} label="New Invoice" to="/finance/billing" accent="#10B981" />}
        </div>
      </div>

      {/* ALERTS ROW */}
      {(alerts.overdue_receivables || alerts.pending_vouchers || alerts.overdue_tasks) ? (
        <div className="mb-8" data-testid="alerts-row">
          <div className="font-mono-shelv text-[10px] tracking-[0.25em] uppercase text-[#D90429] font-bold mb-3 flex items-center gap-2">
            <Warning size={12} weight="fill" /> Attention Required
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <AlertPill testId="alert-overdue-receivables" count={alerts.overdue_receivables} amount={alerts.overdue_amount} color="#D90429" label="Overdue Receivables" onClick={() => navigate("/finance")} />
            <AlertPill testId="alert-pending-vouchers" count={alerts.pending_vouchers} color="#F59E0B" label="Pending Vouchers" onClick={() => navigate("/finance/voucher-ai")} />
            <AlertPill testId="alert-overdue-tasks" count={alerts.overdue_tasks} color="#7C3AED" label="Unacked Overdue Tasks" onClick={() => navigate("/tracker")} />
          </div>
        </div>
      ) : null}

      {/* DIVISION TILES */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="font-mono-shelv text-[10px] tracking-[0.25em] uppercase text-[#717680] font-bold">Divisions</div>
          <h2 className="font-display text-2xl font-black tracking-tight mt-1">Your operating surface</h2>
        </div>
        {loading && <div className="text-xs text-[#717680] font-mono-shelv">LOADING…</div>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="division-grid">
        {showCrm && (
          <DivisionTile
            id="crm"
            icon={Users}
            primary={{ to: "/dashboard/sales" }}
            kpis={[
              { label: "Hot leads",    value: d.crm?.hot_leads ?? 0, hint: `${d.crm?.total_leads ?? 0} total` },
              { label: "Pipeline",     value: formatINR(d.crm?.pipeline_value || 0) },
              { label: "Proposals",    value: d.crm?.total_proposals ?? 0 },
              { label: "Won",          value: d.crm?.won ?? 0, hint: "closed deals" },
            ]}
            ctas={[
              { label: "Leads", to: "/leads" },
              { label: "Sales Pipeline", to: "/dashboard/sales" },
              { label: "AI Swarm", to: "/swarm" },
            ]}
          />
        )}

        {showFinance && (
          <DivisionTile
            id="finance"
            icon={Wallet}
            primary={{ to: "/finance" }}
            kpis={[
              { label: "Money in · 7d", value: formatINR(d.finance?.money_in_week || 0), hint: `${d.finance?.money_in_count ?? 0} payments` },
              { label: "Overdue",       value: formatINR(d.finance?.overdue_total || 0), hint: `${d.finance?.overdue_count ?? 0} invoices` },
              { label: "Pending vouchers", value: d.finance?.pending_vouchers ?? 0 },
              { label: "Billing",       value: <ChartPieSlice size={20} weight="bold" className="inline mt-1" />, hint: "invoices · GST" },
            ]}
            ctas={[
              { label: "Overview", to: "/finance" },
              { label: "Voucher AI", to: "/finance/voucher-ai" },
              { label: "Billing", to: "/finance/billing" },
            ]}
          />
        )}

        {showCrm && (
          <DivisionTile
            id="procurement"
            icon={Cpu}
            primary={{ to: "/procurement" }}
            kpis={[
              { label: "Item master", value: d.procurement?.total_items ?? 0, hint: "SKUs tracked" },
              { label: "Vendors",     value: d.procurement?.total_vendors ?? 0 },
              { label: "Live margins", value: "AI", hint: "real-time" },
              { label: "AI Auto-fetch", value: <Sparkle size={20} weight="fill" className="inline mt-1 text-[#F59E0B]" />, hint: "Gemini parses PDFs" },
            ]}
            ctas={[
              { label: "Procurement Intel", to: "/procurement" },
              { label: "Purchases", to: "/finance/purchases" },
            ]}
          />
        )}

        {showHr && (
          <DivisionTile
            id="hr"
            icon={Trophy}
            primary={{ to: "/leaderboard" }}
            kpis={[
              { label: "Active employees", value: d.hr?.employee_count ?? 0 },
              { label: "Top performer",    value: d.hr?.top_performer || "—", hint: d.hr?.top_performer ? `${d.hr?.period} · score ${Math.round(d.hr?.top_score || 0)}` : "run leaderboard →" },
              { label: "Leaderboard",      value: <Trophy size={20} weight="fill" className="inline mt-1 text-[#F59E0B]" /> },
              { label: "HR letters",       value: <Sparkle size={20} weight="bold" className="inline mt-1 text-[#7C3AED]" /> },
            ]}
            ctas={[
              { label: "Leaderboard", to: "/leaderboard" },
              { label: "HR Letters", to: "/hr/letters" },
              { label: "Onboarding", to: "/hr/onboarding" },
            ]}
          />
        )}

        {showMis && (
          <DivisionTile
            id="mis"
            icon={Scan}
            primary={{ to: "/mis" }}
            kpis={[
              { label: "Today · scanned", value: (d.mis?.today_scanned ?? 0).toLocaleString("en-IN") },
              { label: "Today · QC'd",    value: (d.mis?.today_qc ?? 0).toLocaleString("en-IN") },
              { label: "Month · pages",   value: (d.mis?.month_scanned ?? 0).toLocaleString("en-IN") },
              { label: "Targets",         value: <TargetIcon size={20} weight="bold" className="inline mt-1 text-[#3B82F6]" /> },
            ]}
            ctas={[
              { label: "Daily MIS", to: "/mis/daily" },
              { label: "Monthly Report", to: "/mis" },
              { label: "Targets", to: "/mis/targets" },
            ]}
          />
        )}

        {showTracker && (
          <DivisionTile
            id="tracker"
            icon={TargetIcon}
            primary={{ to: "/tracker" }}
            kpis={[
              { label: "Open tasks",        value: d.tracker?.open_tasks ?? 0 },
              { label: "Overdue · unacked", value: d.tracker?.overdue_unacked ?? 0, hint: d.tracker?.overdue_unacked ? "needs clarification" : "all clear" },
              { label: "Deadline HUD",      value: <Warning size={20} weight="bold" className="inline mt-1 text-[#D90429]" /> },
              { label: "Accountability",    value: "24/7" },
            ]}
            ctas={[
              { label: "Task Watchlist", to: "/tracker" },
            ]}
          />
        )}

        {isAdmin && (
          <DivisionTile
            id="admin"
            icon={ShieldCheck}
            primary={{ to: "/admin/users" }}
            kpis={[
              { label: "Users",             value: d.admin?.total_users ?? 0 },
              { label: "Pending approval",  value: d.admin?.pending_approval ?? 0, hint: d.admin?.pending_approval ? "review roles" : "none" },
              { label: "Policies",          value: <ShieldCheck size={20} weight="bold" className="inline mt-1 text-[#B45309]" /> },
              { label: "RBAC",              value: "5 roles" },
            ]}
            ctas={[
              { label: "Users & Roles", to: "/admin/users" },
              { label: "Policies", to: "/hr/policies" },
            ]}
          />
        )}
      </div>

      {/* Footer signature */}
      <div className="mt-10 pt-6 border-t border-black/10 flex flex-wrap items-center justify-between gap-3 text-xs text-[#717680]">
        <div className="font-mono-shelv tracking-widest uppercase">Reyansh by Shelvestech · Delivering Better Healthcare</div>
        <button
          data-testid="cta-classic-dashboard"
          onClick={() => navigate("/dashboard/sales")}
          className="text-[#D90429] font-semibold hover:underline flex items-center gap-1"
        >
          Open classic Sales Dashboard <ArrowRight size={12} weight="bold" />
        </button>
      </div>
    </Layout>
  );
}
